import { useTranslation } from "react-i18next"
import { useEffect, useRef } from "react"

interface ConfirmationModalProps {
  isOpen: boolean
  onConfirm: () => void
  onCancel: () => void
}

const ConfirmationModal = ({
  isOpen,
  onConfirm,
  onCancel,
}: ConfirmationModalProps) => {
  const { t } = useTranslation()
  const confirmButtonRef = useRef<HTMLButtonElement>(null)
  
  useEffect(() => {
    if (isOpen) {
      // Focus the confirm button when modal opens
      confirmButtonRef.current?.focus()
      
      // Trap focus within modal
      const handleTabKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onCancel()
          e.preventDefault()
        }
        
        if (e.key === 'Tab') {
          const focusableElements = document.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
          const firstElement = focusableElements[0] as HTMLElement
          const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement
          
          if (e.shiftKey && document.activeElement === firstElement) {
            lastElement.focus()
            e.preventDefault()
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            firstElement.focus()
            e.preventDefault()
          }
        }
      }
      
      document.addEventListener('keydown', handleTabKey)
      return () => {
        document.removeEventListener('keydown', handleTabKey)
      }
    }
  }, [isOpen, onCancel])
  
  if (!isOpen) return null

  return (
    <>
      <div 
        className="fixed inset-0 bg-theme_background opacity-50 z-40" 
        aria-hidden="true"
      />
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirmation-title"
        aria-describedby="confirmation-description"
      >
        <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
          <h3 
            className="text-lg font-semibold mb-4 text-black"
            id="confirmation-title"
          >
            {t("general.are_you_sure")}
          </h3>
          <p 
            className="mb-6 text-gray-600"
            id="confirmation-description"
          >
            Are you sure you want to leave checkout? The items you've selected
            may not be available later.
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              aria-label={t("general.cancel")}
            >
              {t("general.cancel")}
            </button>
            <button
              ref={confirmButtonRef}
              onClick={onConfirm}
              className="px-4 py-2 bg-theme_accent text-theme_accent_contrast rounded hover:bg-theme_accent_light"
              aria-label={t("general.leave")}
            >
              {t("general.leave")}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export { ConfirmationModal }
